import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import T from '../theme';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import { Tag, PageHero, SectionWrap, Btn } from '../components/shared';

const F = "'Plus Jakarta Sans', sans-serif";
const MONO = "'DM Mono', monospace";

/* ── PER-SERVICE CUSTOMIZATION ────────────────────────────────────────────── */
const SERVICE_CONTENT = {
  template: {
    dropdownValue: 'unsure',
    tag: 'Building a New Website',
    title: <>Let's pick the right<br /><span style={{ color: T.aquaDark }}>template & features.</span></>,
    sub: "Tell us a bit about your business and goals. We'll help you choose the right starting point instead of guessing.",
  },
  modernize: {
    dropdownValue: 'audit',
    tag: 'Modernizing a Digital Product',
    title: <>Let's modernize your<br /><span style={{ color: T.aquaDark }}>existing product.</span></>,
    sub: "Share what's live today and what feels outdated or clunky. We'll scope a review that improves usability without breaking what already works.",
  },
  mvp: {
    dropdownValue: 'kickstart',
    tag: 'Startup UX Kickstart',
    title: <>Let's get your MVP<br /><span style={{ color: T.aquaDark }}>ready for users.</span></>,
    sub: "Tell us where your idea stands. We'll help refine the flow and validate it with light user testing before you build further.",
  },
};

const DEFAULT_HERO = {
  tag: "Let's Talk",
  title: <>Free 15-min<br /><span style={{ color: T.aquaDark }}>UX check-in.</span></>,
  sub: "No pitch. No commitment. We'll listen to what you're dealing with and tell you honestly what's most valuable for your situation, even if it's not us.",
};

function Field({ label, required, children }) {
  return (
    <div>
      <label
        style={{
          fontSize: '0.8rem',
          color: T.muted,
          display: 'block',
          marginBottom: 7,
          fontFamily: MONO,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {label}{required && ' *'}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const [searchParams] = useSearchParams();
  const serviceKey = searchParams.get('service');
  const custom = serviceKey ? SERVICE_CONTENT[serviceKey] : null;

  const [form, setForm] = useState(() => ({
    name: '',
    email: '',
    company: '',
    service: custom?.dropdownValue || '',
    budget: '',
    message: '',
  }));

  const [sent, setSent] = useState(false);
  const [focus, setFocus] = useState('');

  const base = {
    width: '100%',
    padding: '14px 16px',
    background: T.surface,
    border: `1.5px solid ${T.borderGray}`,
    borderRadius: 8,
    color: T.ink,
    fontSize: '1rem',
    fontFamily: F,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  };

  const inp = (name) => ({
    ...base,
    ...(focus === name
      ? {
          borderColor: T.aqua,
          boxShadow: `0 0 0 3px ${T.aquaGhost}`,
        }
      : {}),
  });

  const NEXT_STEPS = [
    {
      icon: 'edit_note',
      text: 'Submit this form or email us at hello@uxtudio.co',
    },
    {
      icon: 'calendar_month',
      text: 'We respond within 24 hours to book your check-in call',
    },
    {
      icon: 'chat_bubble',
      text: 'A 15-minute conversation about your situation, no pitch',
    },
    {
      icon: 'description',
      text: 'You receive a scoped, fixed-price proposal within 24 hours',
    },
  ];

  const REASSURANCES = [
    { icon: 'star', text: 'Free first call, always' },
    { icon: 'schedule', text: 'Reply within 24 hours' },
    { icon: 'receipt_long', text: 'Fixed prices, no surprises' },
    {
      icon: 'swap_horiz',
      text: "We'll refer you elsewhere if we're not the right fit",
    },
  ];

  const hero = custom || DEFAULT_HERO;

  return (
    <div style={{ background: T.bg }}>
      {/* Responsive styles */}
      <style>{`
        .contact-section {
          padding-top: 72px;
        }

        .contact-layout {
          display: grid;
          grid-template-columns: 1fr 1.4fr;
          gap: 88px;
          align-items: start;
        }

        .contact-form {
          display: grid;
          gap: 20px;
          background: ${T.surface};
          padding: 40px;
          border-radius: 14px;
          border: 1.5px solid ${T.borderGray};
          box-shadow: 0 2px 16px rgba(11,31,58,0.07);
        }

        .contact-name-email {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .contact-success {
          text-align: center;
          padding: 72px 44px;
          background: ${T.surface};
          border: 1.5px solid ${T.aqua};
          border-radius: 14px;
          box-shadow: 0 0 0 6px ${T.aquaGhost};
        }

        .contact-step {
          display: flex;
          gap: 16px;
          margin-bottom: 26px;
          align-items: flex-start;
        }

        .contact-step-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          background: ${T.aquaGhost};
          border: 1px solid ${T.border};
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .contact-direct-email {
          margin-top: 36px;
          padding: 24px 28px;
          background: ${T.aquaGhost};
          border: 1.5px solid ${T.border};
          border-radius: 10px;
        }

        .contact-reassurances {
          margin-top: 24px;
        }

        .contact-reassurance {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }

        @media (max-width: 900px) {
          .contact-layout {
            grid-template-columns: 1fr;
            gap: 56px;
          }
        }

        @media (max-width: 600px) {
          .contact-section {
            padding-top: 44px;
          }

          .contact-layout {
            gap: 44px;
          }

          .contact-form {
            padding: 24px 18px;
            gap: 18px;
            border-radius: 12px;
          }

          .contact-name-email {
            grid-template-columns: 1fr;
            gap: 18px;
          }

          .contact-step {
            gap: 12px;
            margin-bottom: 22px;
          }

          .contact-step-icon {
            width: 34px;
            height: 34px;
          }

          .contact-step p {
            font-size: 0.94rem !important;
            line-height: 1.65 !important;
            padding-top: 5px !important;
          }

          .contact-direct-email {
            margin-top: 28px;
            padding: 20px;
          }

          .contact-reassurances {
            margin-top: 20px;
          }

          .contact-success {
            padding: 48px 22px;
          }

          .contact-success h3 {
            font-size: 1.55rem !important;
          }

          .contact-success p {
            font-size: 0.94rem !important;
          }
        }

        @media (max-width: 380px) {
          .contact-form {
            padding: 20px 14px;
          }

          .contact-direct-email {
            padding: 18px;
          }
        }
      `}</style>

      <PageHero
        tag={hero.tag}
        title={hero.title}
        sub={hero.sub}
      />

      <SectionWrap className="contact-section">
        <div className="contact-layout">

          {/* Left */}
          <Reveal variant="left">
            <Tag style={{ marginBottom: 22 }}>
              What happens next
            </Tag>

            {NEXT_STEPS.map((s, i) => (
              <div key={i} className="contact-step">
                <div className="contact-step-icon">
                  <Icon
                    name={s.icon}
                    size={17}
                    color={T.aquaDark}
                    fill={1}
                  />
                </div>

                <p
                  style={{
                    fontSize: '1rem',
                    color: T.muted,
                    lineHeight: 1.75,
                    paddingTop: 6,
                    margin: 0,
                  }}
                >
                  {s.text}
                </p>
              </div>
            ))}

            <div className="contact-direct-email">
              <div
                style={{
                  fontFamily: MONO,
                  fontSize: '0.7rem',
                  color: T.aquaDark,
                  marginBottom: 8,
                }}
              >
                Direct email
              </div>

              <div
                style={{
                  fontFamily: F,
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: T.ink,
                  overflowWrap: 'anywhere',
                }}
              >
                hello@uxtudio.co
              </div>
            </div>

            <div className="contact-reassurances">
              {REASSURANCES.map((item, i) => (
                <div key={i} className="contact-reassurance">
                  <Icon
                    name={item.icon}
                    size={17}
                    color={T.aquaDark}
                    fill={1}
                  />

                  <p
                    style={{
                      fontSize: '0.92rem',
                      color: T.muted,
                      lineHeight: 1.7,
                      margin: 0,
                    }}
                  >
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Form */}
          <Reveal variant="up" delay="0.1s">
            {sent ? (
              <div className="contact-success">
                <Icon
                  name="check_circle"
                  size={56}
                  color={T.aquaDark}
                  fill={1}
                  style={{ marginBottom: 18 }}
                />

                <h3
                  style={{
                    fontFamily: F,
                    fontWeight: 800,
                    fontSize: '1.9rem',
                    color: T.ink,
                    marginBottom: 12,
                  }}
                >
                  We'll be in touch!
                </h3>

                <p
                  style={{
                    color: T.muted,
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    margin: 0,
                  }}
                >
                  Expect a reply within 24 hours to schedule your free
                  15-minute check-in.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setSent(true);
                }}
                className="contact-form"
              >
                <div className="contact-name-email">
                  <Field label="Your name" required>
                    <input
                      required
                      style={inp('name')}
                      onFocus={() => setFocus('name')}
                      onBlur={() => setFocus('')}
                      value={form.name}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          name: e.target.value,
                        })
                      }
                      placeholder="Jane Smith"
                    />
                  </Field>

                  <Field label="Email" required>
                    <input
                      required
                      type="email"
                      style={inp('email')}
                      onFocus={() => setFocus('email')}
                      onBlur={() => setFocus('')}
                      value={form.email}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          email: e.target.value,
                        })
                      }
                      placeholder="jane@company.com"
                    />
                  </Field>
                </div>

                <Field label="Company / Organisation">
                  <input
                    style={inp('company')}
                    onFocus={() => setFocus('company')}
                    onBlur={() => setFocus('')}
                    value={form.company}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        company: e.target.value,
                      })
                    }
                    placeholder="Optional"
                  />
                </Field>

                <Field label="What do you need help with?" required>
                  <select
                    required
                    style={{
                      ...inp('service'),
                      appearance: 'none',
                    }}
                    onFocus={() => setFocus('service')}
                    onBlur={() => setFocus('')}
                    value={form.service}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        service: e.target.value,
                      })
                    }
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    <option value="audit">
                      UX Heuristic Audit ($300–$800)
                    </option>
                    <option value="workflow">
                      Workflow Improvement ($800–$2,500)
                    </option>
                    <option value="kickstart">
                      Startup UX Kickstart ($1,200–$3,500)
                    </option>
                    <option value="research">
                      User Research & Testing ($500–$2,000)
                    </option>
                    <option value="implementation">
                      UX + Dev Implementation ($500–$8,000)
                    </option>
                    <option value="support">
                      Monthly UX Support ($400–$1,500/mo)
                    </option>
                    <option value="unsure">
                      Not sure, need advice
                    </option>
                  </select>
                </Field>

                <Field label="Budget range">
                  <select
                    style={{
                      ...inp('budget'),
                      appearance: 'none',
                    }}
                    onFocus={() => setFocus('budget')}
                    onBlur={() => setFocus('')}
                    value={form.budget}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        budget: e.target.value,
                      })
                    }
                  >
                    <option value="">Select a range</option>
                    <option value="under-1k">Under $1,000</option>
                    <option value="1k-3k">$1,000–$3,000</option>
                    <option value="3k-8k">$3,000–$8,000</option>
                    <option value="8k-plus">$8,000+</option>
                    <option value="monthly">Monthly retainer</option>
                    <option value="not-sure">Not sure yet</option>
                  </select>
                </Field>

                <Field label="Tell us about your project" required>
                  <textarea
                    required
                    rows={5}
                    style={{
                      ...inp('message'),
                      resize: 'vertical',
                    }}
                    onFocus={() => setFocus('message')}
                    onBlur={() => setFocus('')}
                    value={form.message}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        message: e.target.value,
                      })
                    }
                    placeholder="What's the situation? What are you trying to improve or build?"
                  />
                </Field>

                <Btn
                  type="submit"
                  size="lg"
                  style={{
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  Submit Request →
                </Btn>

                <p
                  style={{
                    fontSize: '0.85rem',
                    color: T.slate,
                    textAlign: 'center',
                    margin: 0,
                  }}
                >
                  We reply within 24 hours. No spam, ever.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </SectionWrap>
    </div>
  );
}